import { useId, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useLanguage } from '@/i18n/LanguageContext';
import { CANCEL_REASON_CODES, type CancelOrderResult, type CancelReasonCode } from '@/api/types';
import { ActionError, cancelReasonLabel, describeActionError, newOpId } from '@/pages/Exceptions/shared';

/** States where cancelling releases reservations only; later states leave picked goods in staging/packing. */
const BEFORE_PICK = new Set(['NEW', 'RESERVED', 'ON_HOLD']);

/**
 * Cancel an order (mode CANCEL) or finish a cancellation after the goods
 * were put back (mode FINISH = cancel again with goodsReturned=true).
 * Reason -> explicit consequence -> confirm. The resulting state is shown
 * by the order page after `onDone`.
 */
export function CancelOrderDialog({
  orderId, number, state, mode, onClose, onDone,
}: {
  orderId: string;
  number: string;
  state: string;
  mode: 'CANCEL' | 'FINISH';
  onClose: () => void;
  onDone: (result: CancelOrderResult) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const [reason, setReason] = useState<CancelReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opId = useRef(newOpId());
  const touch = (): void => {
    opId.current = newOpId();
  };

  const consequence =
    mode === 'FINISH'
      ? t('ordCancel.finishText')
      : BEFORE_PICK.has(state)
        ? t('ordCancel.consequenceRelease')
        : t('ordCancel.consequencePicked');

  async function submit(): Promise<void> {
    if (busy) return;
    if (!reason) return setFormError(t('stx.reasonMissing'));
    if (reason === 'OTHER' && note.trim().length < 3) return setFormError(t('stx.noteMissing'));
    setFormError(null);
    setSubmitError(null);
    setBusy(true);
    try {
      const res = await endpoints.cancelOrder({
        opId: opId.current, orderId, reasonCode: reason, ...(note.trim() ? { note: note.trim() } : {}),
        ...(mode === 'FINISH' ? { goodsReturned: true } : {}),
      });
      onDone(res);
    } catch (err) {
      setSubmitError(describeActionError(t, err));
      setBusy(false);
    }
  }

  const title = mode === 'FINISH' ? t('ordCancel.finishTitle', { number }) : t('ordCancel.title', { number });

  return (
    <section className="card stock-panel" role="dialog" aria-label={title}>
      <h2 className="card-title">{title}</h2>
      <div className="card danger-card" data-testid="cancel-consequence">
        <p style={{ margin: 0, fontWeight: 600 }}>{consequence}</p>
      </div>
      <form
        className="order-create"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field-row">
          <label htmlFor={`${uid}-reason`}>{t('stx.reason')}</label>
          <select
            id={`${uid}-reason`}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value as CancelReasonCode | '');
              touch();
            }}
          >
            <option value="">{t('stx.reasonPlaceholder')}</option>
            {CANCEL_REASON_CODES.map((r) => (
              <option key={r} value={r}>
                {cancelReasonLabel(t, r)}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <label htmlFor={`${uid}-note`}>{reason === 'OTHER' ? t('stx.noteRequired') : t('stx.note')}</label>
          <textarea
            id={`${uid}-note`}
            rows={2}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              touch();
            }}
          />
        </div>
        {formError && (
          <div className="field-error" role="alert">
            {formError}
          </div>
        )}
        {submitError && <ActionError message={submitError} />}
        <div className="form-actions">
          <button type="submit" className={mode === 'FINISH' ? 'primary' : 'danger'} disabled={busy}>
            {busy ? t('stx.working') : mode === 'FINISH' ? t('ordCancel.finishConfirm') : t('ordCancel.confirm')}
          </button>
          <button type="button" onClick={onClose} disabled={busy}>
            {mode === 'FINISH' ? t('stx.cancel') : t('ordCancel.keep')}
          </button>
        </div>
      </form>
    </section>
  );
}
